import type { FC, ReactElement } from 'react';

import type { IconColor, IconList } from '@snowit/icetypes';

import testId from '@snowit/utils/testId';

import Icon from '../Icon';
import Typo from '../Typography/Typo';

import cx from 'classnames';
import s from './Map.module.scss';

export interface IMapPin {
  /** Id of the component */
  id?: string;
  /** Add a classname to the map pin */
  className?: string;
  /** Number of the map pin */
  number?: number;
  /** Label on the pin */
  label?: string;
  showLabel?: boolean;
  icon?: IconList;
  iconSize?: number;
  iconColor?: string;
  iconRadius?: boolean;
  /** Jest/Cypress test string */
  test?: string;
  highlighted?: boolean;
  onClick?: (input: any) => void;
  value?: any;
}

/**
  MapPin is an informational visual element used to show the number of a corresponding element on a map or icon if the number is not present.
*/
const MapPin: FC<IMapPin> = (props: IMapPin): ReactElement => {
  const {
    id,
    className,
    icon,
    iconColor,
    iconSize = 1.5,
    iconRadius,
    number,
    label,
    showLabel,
    test,
    highlighted,
    value,
    onClick,
  } = props;

  const handleClick = () => {
    if (typeof onClick === 'function') {
      onClick(value);
    }
  };

  const returnLabel = () => {
    return (
      <div
        onClick={handleClick}
        id={id}
        className={cx(s.pinLabel, className, {
          [s.showLabel]: showLabel,
          [s.highlighted]: highlighted,
        })}
        {...testId(test)}
      >
        {showLabel && (
          <Typo
            className={s.label}
            tag={'span'}
            size={'sm'}
            weight={'medium'}
            color={'text'}
            text={String(label || number)}
          />
        )}
      </div>
    );
  };

  const returnIcon = (iconName: IconList = 'map_pin') => {
    return (
      <Icon
        className={cx(s.pinIcon, className, {
          [s.highlighted]: highlighted,
          [s.withRadius]: iconRadius,
        })}
        onClick={handleClick}
        id={id}
        color={iconRadius ? 'primary' : (iconColor as IconColor)}
        sizeV2={iconRadius ? [iconSize * 2.5] : [iconSize]}
        name={iconName}
        {...testId(test)}
      />
    );
  };

  if (icon || (!number && !label)) {
    return returnIcon(icon);
  }

  return returnLabel();
};

export default MapPin;
